import { Box, VStack, Button, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Image, HStack, Collapse, Icon, Tooltip } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
  /** Desktop collapse state (md+) */
  isCollapsed?: boolean
}

const NavIcon = memo(({ icon }: { icon: string }) => (
  <Box display="inline-flex" alignItems="center" justifyContent="center" w="20px" h="20px" flexShrink={0}>
    {renderMenuIcon(icon)}
  </Box>
))
NavIcon.displayName = 'NavIcon'

type SidebarLogoProps = {
  logoUrl: string
  appName: string
  isCollapsed?: boolean
}

const SidebarLogo = memo(({ logoUrl, appName, isCollapsed }: SidebarLogoProps) => {
  const logoSrc = isCollapsed ? '/assets/img/als-white-icon-32x32.png' : logoUrl
  return (
    <Box
      pt={isCollapsed ? 2 : 4}
      px={isCollapsed ? 2 : 5}
      pb={isCollapsed ? 1 : 4}
      flexShrink={0}
      bg="var(--sidebar-bg)"
      position="sticky"
      top={0}
      zIndex={1}
    >
      <HStack
        spacing={isCollapsed ? 0 : 2}
        align="center"
        overflow="hidden"
        w="full"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}
      >
        <Image
          key={logoSrc}
          src={logoSrc}
          alt="ALS Logo"
          height="32px"
          width={isCollapsed ? '32px' : 'auto'}
          objectFit="contain"
          loading="eager"
          flexShrink={0}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            if (!isCollapsed && target.src !== '/assets/img/als-white-logo.png') {
              target.src = '/assets/img/als-white-logo.png'
            }
          }}
        />
        {!isCollapsed && appName ? (
          <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex="1" minW={0}>
            | {appName}
          </Text>
        ) : null}
      </HStack>
    </Box>
  )
})
SidebarLogo.displayName = 'SidebarLogo'

function SidebarComponent({ items, isOpen = false, onClose, isCollapsed = false }: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()

  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  const borderColor = useThemeColors().border
  const { settings: siteSettings } = useSiteSettings()

  const collapsedW = '64px'
  const expandedW = '240px'

  // Initialize openGroups from localStorage to persist across re-renders
  // Use lazy initializer to only read from localStorage once on mount
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch (e) {
      // Ignore parse errors
    }
    return {}
  })

  // Track groups the user has manually closed (don't auto-open on route change)
  const manuallyClosedRef = useRef<Set<string>>(new Set())

  // Memoize database-driven values to prevent recalculation on every render
  // Extract values directly to prevent object reference changes
  const sidenavLogo = siteSettings?.sidenav_logo

  // Add cache busting query parameter to logo URL (only when URL changes)
  const logoUrl = useMemo(() => {
    const baseUrl = sidenavLogo || '/assets/img/als-white-logo.png'
    // Add version for cache busting (static version that changes only when logo changes)
    // In development, use a hash of the URL; in production, use build time
    const version = import.meta.env.DEV
      ? `?v=${baseUrl.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'dev'}`
      : `?v=${import.meta.env.VITE_BUILD_TIME || '1.0.0'}`
    return baseUrl.includes('?') ? baseUrl : `${baseUrl}${version}`
  }, [sidenavLogo])
  const appName = useMemo(() =>
    import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
    [] // Empty dependency array since env variable doesn't change
  )

  const sidebarBg = 'var(--sidebar-bg)'
  const sidebarText = 'var(--sidebar-text)'
  const sidebarHoverBg = 'var(--sidebar-hover-bg)'
  const sidebarActiveBg = 'var(--sidebar-active-bg)'

  // Memoize helper functions to prevent recreation on every render
  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])

  // Memoize routes to prevent unnecessary recalculations and re-renders
  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []

    // Debug logging to help diagnose navigation issues
    if (routes.length === 0 && userRoutes) {
      console.warn('[Sidebar] No routes found. userRoutes:', userRoutes)
    } else if (routes.length > 0) {
      console.log('[Sidebar] Total routes:', routes.length, 'Routes:', routes)
    }

    const filtered = routes.filter((r: any) => {
      // A parent route is included if its own side_nav is 'true'
      // OR if it has at least one child with side_nav 'true'
      const hasDisplayableChildren = Array.isArray(r.children) && r.children.some((c: any) => c.side_nav === 'true')
      return r.side_nav === 'true' || hasDisplayableChildren
    })

    if (filtered.length === 0 && routes.length > 0) {
      console.warn('[Sidebar] No routes with side_nav=true. All routes:', routes.map((r: any) => ({
        name: r.name,
        path: r.path,
        side_nav: r.side_nav
      })))
    }

    return filtered
  }, [userRoutes])

  const isActivePath = useCallback((targetPath: string) => {
    const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
    const cur = norm(pathname)
    const tgt = norm(targetPath)
    return cur === tgt || cur.startsWith(tgt + '/')
  }, [pathname])

  useEffect(() => {
    // Auto-open the parent whose child is active (accordion: only one group open)
    // Respect manually closed groups (don't auto-open them even if they have active children)
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      if (manuallyClosedRef.current.has(groupKey)) return
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) => isActivePath(joinPaths(route.path, c.path)))
        if (shouldOpen || isActivePath(route.path)) groupsToOpen[groupKey] = true
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })

    // Accordion: at most one group open; the one containing the active route, or none
    const keysToOpen = Object.keys(groupsToOpen)
    const soleKey = keysToOpen.length > 0 ? keysToOpen[0] : null
    const next = soleKey ? { [soleKey]: true } : {}

    setOpenGroups((prev) => {
      const prevOpen = Object.keys(prev).find((k) => prev[k])
      if ((soleKey || null) === (prevOpen || null)) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, joinPaths])

  // Helper to check if parent has active child
  const hasActiveChild = useCallback((route: any) => {
    if (!Array.isArray(route.children)) return false
    return route.children.some((c: any) => {
      if (c.side_nav !== 'true') return false
      const childPath = joinPaths(route.path, c.path)
      return isActivePath(childPath)
    })
  }, [isActivePath, joinPaths])

  const renderNavButtons = useCallback(
    (routes: any[]) =>
      routes.map((route: any) => {
        const displayableChildren = Array.isArray(route.children)
          ? route.children.filter((c: any) => c.side_nav === 'true')
          : []
        const hasDisplayableChildren = displayableChildren.length > 0
        const shouldShowChildren = !isCollapsed && hasDisplayableChildren

        if (route.side_nav === 'true' && !hasDisplayableChildren) {
          const isRouteActive = isActivePath(route.path)
          const btn = (
            <Button
              key={route.path}
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'flex-start'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w={isCollapsed ? '100%' : undefined}
              onClick={(e) => {
                e.preventDefault()
                navigate(route.path)
                onClose?.()
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isRouteActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isRouteActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack spacing={3}>
                  <NavIcon icon={route.icon} />
                  <Text>{route.name}</Text>
                </HStack>
              )}
            </Button>
          )
          return isCollapsed ? (
            <Tooltip key={route.path} label={route.name} placement="right" hasArrow>
              <Box w="100%">{btn}</Box>
            </Tooltip>
          ) : (
            btn
          )
        }

        if (hasDisplayableChildren) {
          const groupKey = route.path || route.name
          const groupIsOpen = !!openGroups[groupKey]
          const isParentActive = isActivePath(route.path) || hasActiveChild(route)

          const parentBtn = (
            <Button
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'space-between'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w="100%"
              pr={isCollapsed ? 1.5 : 4}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (isCollapsed) {
                  if (route.path) navigate(route.path)
                  onClose?.()
                  return
                }
                setOpenGroups((prev) => {
                  const newState = !prev[groupKey]
                  if (newState) {
                    manuallyClosedRef.current.delete(groupKey)
                    const updated = { [groupKey]: true }
                    try {
                      localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                    } catch {
                      //
                    }
                    return updated
                  }
                  manuallyClosedRef.current.add(groupKey)
                  const updated = { ...prev, [groupKey]: false }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  return updated
                })
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isParentActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isParentActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack w="full" justify="space-between" spacing={3}>
                  <HStack spacing={3}>
                    <NavIcon icon={route.icon} />
                    <Text>{route.name}</Text>
                  </HStack>
                  <Icon as={groupIsOpen ? ChevronDown : ChevronRight} />
                </HStack>
              )}
            </Button>
          )

          return (
            <Box key={route.path} w="100%">
              {isCollapsed ? (
                <Tooltip label={route.name} placement="right" hasArrow>
                  <Box w="100%">{parentBtn}</Box>
                </Tooltip>
              ) : (
                parentBtn
              )}
              {shouldShowChildren ? (
                <Collapse in={groupIsOpen} animateOpacity>
                  <VStack spacing={1} pl={6} align="stretch" mt={1}>
                    {displayableChildren.map((child: any) => {
                      const childPath = child.path?.startsWith('/')
                        ? child.path
                        : joinPaths(route.path, child.path)
                      const isChildActive = isActivePath(childPath)
                      return (
                        <Button
                          key={child.path}
                          variant="ghost"
                          justifyContent="flex-start"
                          leftIcon={renderMenuIcon(child.icon)}
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(childPath)
                            onClose?.()
                          }}
                          color={sidebarText}
                          _hover={{ bg: sidebarHoverBg }}
                          bg={isChildActive ? sidebarActiveBg : 'transparent'}
                          fontWeight={isChildActive ? 'semibold' : 'normal'}
                        >
                          {child.name}
                        </Button>
                      )
                    })}
                  </VStack>
                </Collapse>
              ) : null}
            </Box>
          )
        }
        return null
      }),
    [
      openGroups,
      isActivePath,
      hasActiveChild,
      joinPaths,
      navigate,
      sidebarText,
      sidebarHoverBg,
      sidebarActiveBg,
      isCollapsed,
      onClose,
    ],
  )

  const fallbackItems = useMemo(
    () =>
      (items || []).map((it) => {
        const btn = (
          <Button
            key={it.href}
            variant="ghost"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            alignItems="center"
            px={isCollapsed ? 1.5 : 4}
            py={isCollapsed ? 1.5 : 2}
            minH={isCollapsed ? '36px' : undefined}
            w={isCollapsed ? '100%' : undefined}
            leftIcon={!isCollapsed ? renderMenuIcon(it.icon) : undefined}
            onClick={(e) => {
              e.preventDefault()
              navigate(it.href)
              onClose?.()
            }}
            color={sidebarText}
            _hover={{ bg: sidebarHoverBg }}
          >
            {isCollapsed ? <NavIcon icon={it.icon} /> : it.name}
          </Button>
        )
        return isCollapsed ? (
          <Tooltip key={it.href} label={it.name} placement="right" hasArrow>
            <Box w="100%">{btn}</Box>
          </Tooltip>
        ) : (
          btn
        )
      }),
    [items, navigate, sidebarText, sidebarHoverBg, isCollapsed, onClose],
  )

  return (
    <>
      {/* Desktop sidebar */}
      <Box
        w={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        minW={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        transition="width 0.2s ease"
        flexShrink={0}
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        maxH="100vh"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        color={sidebarText}
        overflow="hidden"
      >
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
          <SidebarLogo
            logoUrl={logoUrl}
            appName={appName}
            isCollapsed={isCollapsed}
          />

          {/* Scrollable Navigation Section */}
          <VStack
            spacing={isCollapsed ? 1 : 2}
            align="stretch"
            p={isCollapsed ? 2 : 4}
            pt={isCollapsed ? 1 : 2}
            pr={isCollapsed ? 1 : 2}
            px={isCollapsed ? 1 : 4}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </Box>
      </Box>

      {/* Mobile drawer */}
      <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => { })}>
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg} color={sidebarText}>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack spacing={2} align="center" overflow="hidden" w="full">
              <Image src={logoUrl} alt="ALS Logo" height="24px" objectFit="contain" flexShrink={0} />
              {appName ? (
                <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex="1" minW={0}>
                  | {appName}
                </Text>
              ) : null}
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

// Memoize the component to prevent re-renders when props haven't changed
export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  // Only re-render if isOpen, onClose, or items actually change
  // Use deep comparison for items array to prevent unnecessary re-renders
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual = prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return nextItem && item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
      })
  }

  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    itemsEqual
  )
})

export default Sidebar

